import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"

type UserRole = "member" | "admin"

interface AdminUserFormLabels {
  emailLabel: string
  emailHint: string
  nameLabel: string
  roleLabel: string
  roleHint: string
  roleMember: string
  roleAdmin: string
  save: string
  delete: string
  deleteConfirm: string
}

interface AdminUserFormProps {
  heading: string
  // `null` on the new-user form -- there is no sign-in to report
  // (FR-3.9); the edit form's own sign-in status phrase otherwise, in
  // its sentence-initial register (FR-3.11).
  subtitle: string | null
  backHref: string
  backLabel: string
  // The collection route for a new user (POST) or the user's own address
  // (PATCH) -- distinct from `backHref`, the `AdminContactForm` shape.
  submitHref: string
  // Chooses the HTTP verb explicitly, rather than inferring it from
  // `deleteHref === null` the way `AdminContactForm` does: `impl-delete-
  // self` makes `deleteHref` `null` on one's own record too, which is
  // still an edit and must PATCH (plan R-5).
  isNew: boolean
  // `null` on the new-user form (absent, not merely hidden, keeps
  // «Удалить пользователя» off that screen) and on one's own edit-mode
  // record too (FR-5.2) -- otherwise the user's own address, the same
  // address `submitHref` PATCHes to.
  deleteHref: string | null
  // FR-5.2, FR-5.3: `true` only on one's own record -- disables both role
  // segments (convenience only, NFR-3; the server's own 400 refusal is
  // the enforcement) and, since `useForm` sends state rather than
  // controls, makes the submitted payload omit `role` entirely rather
  // than merely freeze its displayed value (the transform precedent
  // already used by `AdminContactForm.tsx`).
  roleLocked: boolean
  // `null` on the new-user form -- `Identity::User.new` has no name or
  // e-mail yet (`role` alone defaults from the column, never null).
  user: {
    name: string | null
    email: string | null
    role: UserRole
  }
  labels: AdminUserFormLabels
}

// The form's own local state stays flat, keyed by the model's bare
// attribute names (`AdminContactForm.tsx`'s own reasoning): the server's
// failed-save `errors` prop round-trips under these same bare keys, and
// the wire payload nests them under "user" only at submit time (via
// `transform`).
interface UserFormData {
  name: string
  email: string
  role: UserRole
  authenticity_token: string
}

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// docs/design/users.html's Block 3, the two-segment role control's own
// classes -- the contract's exact pair, mirrored from the burger menu's
// own `pillClass` reasoning (Layout.tsx): the only variation between the
// two segments is which of these applies. `disabled:opacity-60` mirrors
// the save button's own disabled treatment -- only takes effect on a
// segment that actually carries the `disabled` attribute (FR-5.2).
function segmentClass(active: boolean): string {
  const base = "flex h-9 flex-1 items-center justify-center rounded-[7px] text-sm disabled:opacity-60"
  return active ? `${base} bg-accent/5 font-medium text-accent` : `${base} text-gray-500`
}

// The user form (`Identity::AdminUsersController#new/#edit`,
// docs/design/users.html's Block 3 verbatim): create and edit share this
// one component, the heading, subtitle and submit verb being the only
// differences (all server-computed) -- the landed `AdminContactForm`
// shape. Field order follows the contract, not FR-3.1's prose order:
// E-mail, Имя, Роль (docs/design/users.html:152-168), with the "no e-mail
// sent" hint under E-mail and the role hint under the segments, in both
// new and edit mode alike -- no mode branching (FR-3.3, FR-3.10).
export default function AdminUserForm({
  heading, subtitle, backHref, backLabel, submitHref, isNew, deleteHref, roleLocked, user, labels,
}: AdminUserFormProps) {
  const form = useForm<UserFormData>({
    name: user.name ?? "",
    email: user.email ?? "",
    role: user.role,
    authenticity_token: csrfToken(),
  })

  // FR-5.3: a disabled segment removes nothing from `useForm`'s own state
  // on its own -- `roleLocked` must omit `role` from the payload itself,
  // not merely disable its display, since the server treats an absent
  // `role` as "unchanged" (FR-5.4).
  form.transform((data) => ({
    user: roleLocked
      ? { name: data.name, email: data.email }
      : { name: data.name, email: data.email, role: data.role },
    authenticity_token: data.authenticity_token,
  }))

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (isNew) {
      form.post(submitHref)
    } else {
      form.patch(submitHref)
    }
  }

  // FR-3.4: the `AdminContactForm.tsx` idiom verbatim -- a browser-native
  // confirm, not a custom dialog; by the time a request reaches the
  // server it is already confirmed.
  function handleDelete(href: string) {
    if (window.confirm(labels.deleteConfirm)) {
      form.delete(href)
    }
  }

  return (
    <AdminLayout>
      <Link href={backHref} className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {backLabel}
      </Link>
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading}</h2>
        {subtitle !== null && (
          <p className="text-[13px] leading-[18px] text-gray-500">{subtitle}</p>
        )}
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.emailLabel}</span>
          <input
            type="email"
            value={form.data.email}
            onChange={(event) => form.setData("email", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.emailHint}</span>
          {form.errors.email && <span className={ERROR_CLASSES}>{form.errors.email}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.nameLabel}</span>
          <input
            type="text"
            value={form.data.name}
            onChange={(event) => form.setData("name", event.target.value)}
            className={INPUT_CLASSES}
          />
          {form.errors.name && <span className={ERROR_CLASSES}>{form.errors.name}</span>}
        </label>
        <fieldset className="flex flex-col gap-1.5">
          <legend className={LABEL_TEXT_CLASSES}>{labels.roleLabel}</legend>
          <div className="flex gap-1 rounded-[10px] border border-gray-300 p-1">
            <button
              type="button"
              disabled={roleLocked}
              aria-pressed={form.data.role === "member"}
              className={segmentClass(form.data.role === "member")}
              onClick={() => form.setData("role", "member")}
            >
              {labels.roleMember}
            </button>
            <button
              type="button"
              disabled={roleLocked}
              aria-pressed={form.data.role === "admin"}
              className={segmentClass(form.data.role === "admin")}
              onClick={() => form.setData("role", "admin")}
            >
              {labels.roleAdmin}
            </button>
          </div>
          <span className={HINT_CLASSES}>{labels.roleHint}</span>
        </fieldset>
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
        {/* FR-3.4: absent, not merely hidden, on the new form and on
            one's own record (server-computed deleteHref). */}
        {deleteHref !== null && (
          <button
            type="button"
            onClick={() => handleDelete(deleteHref)}
            className="flex items-center justify-center gap-1.5 py-1 text-sm font-medium text-red-700"
          >
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9a1.5 1.5 0 001.5 1.4h3.6A1.5 1.5 0 0011.3 13l.7-9M6.7 7v4.5M9.3 7v4.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {labels.delete}
          </button>
        )}
      </form>
    </AdminLayout>
  )
}
